import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { heroService } from '../../services/heroService';
import { uploadImage } from '../../services/imageUpload';
import { Edit, Save, PlusCircle, Trash2, X, UploadCloud, MoveUp, MoveDown } from 'lucide-react';

const HeroContentManager = ({ setEditingHero, updatePageLastUpdated }) => {
  const [heroSlidesContent, setHeroSlidesContent] = useState([]);
  const [loadingHeroContent, setLoadingHeroContent] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchHeroContent = async () => {
      setLoadingHeroContent(true);
      try {
        const slides = await heroService.getHeroSlides();
        setHeroSlidesContent(slides);
      } catch (error) {
        showError('Failed to load Hero content: ' + error.message, 'error');
        setHeroSlidesContent([]);
      } finally {
        setLoadingHeroContent(false);
      }
    };
    fetchHeroContent();
  }, [showError]);

  const handleSaveHeroContent = async () => {
    try {
      const existingSlidesInDb = await heroService.getHeroSlides();
      const existingSlideIds = new Set(existingSlidesInDb.map(s => s.id));
      const slidesToUpdate = heroSlidesContent.filter(s => existingSlideIds.has(s.id));
      const slidesToCreate = heroSlidesContent.filter(s => !existingSlideIds.has(s.id));

      const promises = [];

      for (const slide of slidesToCreate) {
        promises.push(heroService.createHeroSlide(slide));
      }

      for (let i = 0; i < slidesToUpdate.length; i++) {
        const slide = { ...slidesToUpdate[i], order_index: i };
        promises.push(heroService.updateHeroSlide(slide.id, slide));
      }

      await Promise.all(promises);

      showError('Hero content updated successfully!', 'success');
      const updatedSlides = await heroService.getHeroSlides();
      setHeroSlidesContent(updatedSlides.sort((a, b) => a.order_index - b.order_index));
      await updatePageLastUpdated('Home');
    } catch (error) {
      showError('Failed to update Hero content: ' + error.message, 'error');
    }
  };

  const handleHeroSlideChange = (index, field, value) => {
    setHeroSlidesContent(prevSlides =>
      prevSlides.map((slide, i) =>
        i === index ? { ...slide, [field]: value } : slide
      )
    );
  };

  const handleAddHeroSlide = () => {
    setHeroSlidesContent(prevSlides => [
      ...prevSlides,
      { id: null, image: '', title: '', subtitle: '', order_index: prevSlides.length }
    ]);
  };

  const handleRemoveHeroSlide = async (indexToRemove, slideId) => {
    if (slideId) {
      try {
        await heroService.deleteHeroSlide(slideId);
        showError('Hero slide deleted successfully!', 'success');
        setHeroSlidesContent(prevSlides => prevSlides.filter((_, i) => i !== indexToRemove));
        await updatePageLastUpdated('Home');
      } catch (error) {
        showError('Failed to delete hero slide: ' + error.message, 'error');
        const slides = await heroService.getHeroSlides();
        setHeroSlidesContent(slides.sort((a, b) => a.order_index - b.order_index));
      }
    } else {
      setHeroSlidesContent(prevSlides => prevSlides.filter((_, i) => i !== indexToRemove));
    }
  };

  const handleMoveHeroSlide = (fromIndex, toIndex) => {
    setHeroSlidesContent(prevSlides => {
      const newSlides = [...prevSlides];
      const [movedSlide] = newSlides.splice(fromIndex, 1);
      newSlides.splice(toIndex, 0, movedSlide);
      return newSlides.map((slide, index) => ({ ...slide, order_index: index }));
    });
  };

  const handleImageUploadHero = async (event, index, field) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      showError('Uploading image...', 'info');
      const imageUrl = await uploadImage(file);
      handleHeroSlideChange(index, field, imageUrl);
      showError('Image uploaded successfully!', 'success');
    } catch (error) {
      showError('Image upload failed: ' + error.message, 'error');
    }
  };

  if (loadingHeroContent) {
    return (
      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading Hero content editor...</p>
        </div>
      </div>
    );
  }

  if (!heroSlidesContent) {
    return (
      <div className="text-center py-8 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg shadow-md">
        <p className="font-semibold">Error:</p>
        <p>Could not load Hero content for editing. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 p-1 md:p-6 rounded-xl shadow-2xl dark:shadow-xl border border-gray-100 dark:border-gray-700 animate-fade-in">
      <div className="flex justify-between items-center mb-6 border-b pb-4 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Hero Slides</h2>
        <button
          onClick={() => setEditingHero(false)}
          className="flex items-center md:px-4 md:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200 text-sm font-medium shadow-sm"
        >
          <X className="w-4 h-4 mr-2" /> Close
        </button>
      </div>

      <div className="space-y-8">
        {heroSlidesContent.length === 0 && (
          <p className="text-center text-gray-600 dark:text-gray-400">No slides added yet. Click "Add New Slide" to begin.</p>
        )}

        {heroSlidesContent.map((slide, index) => (
          <div key={slide.id || `new-${index}`} className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600 relative">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Slide {index + 1}</h3>
            <div className="absolute top-4 right-4 flex space-x-2">
              {index > 0 && (
                <button
                  onClick={() => handleMoveHeroSlide(index, index - 1)}
                  className="p-2 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 rounded-full hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors duration-200"
                  title="Move Up"
                >
                  <MoveUp className="w-4 h-4" />
                </button>
              )}
              {index < heroSlidesContent.length - 1 && (
                <button
                  onClick={() => handleMoveHeroSlide(index, index + 1)}
                  className="p-2 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 rounded-full hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors duration-200"
                  title="Move Down"
                >
                  <MoveDown className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => handleRemoveHeroSlide(index, slide.id)}
                className="p-2 bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-200 rounded-full hover:bg-red-200 dark:hover:bg-red-700 transition-colors duration-200"
                title="Remove Slide"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor={`hero_image_${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
                {slide.image && (
                  <div className="mb-2">
                    <img src={slide.image} alt={`Slide ${index + 1} Preview`} className="h-40 w-auto object-cover rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-sm">{slide.image}</p>
                  </div>
                )}
                <input
                  type="text"
                  id={`hero_image_${index}`}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200 mb-2"
                  value={slide.image || ''}
                  onChange={(e) => handleHeroSlideChange(index, 'image', e.target.value)}
                  placeholder="Enter image URL or upload below"
                />
                <label className="flex items-center px-4 py-2 bg-accentdark:bg-accent text-accent dark:text-accent rounded-md border border-accent dark:border-accent cursor-pointer hover:bg-accent/30 dark:hover:bg-primary-700 transition duration-200 text-sm font-medium">

                  <UploadCloud className="w-4 h-4 mr-2" /> Upload Image
                  <input type="file" className="hidden" onChange={(e) => handleImageUploadHero(e, index, 'image')} />
                </label>
              </div>
              <div>
                <label htmlFor={`hero_title_${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  id={`hero_title_${index}`}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={slide.title || ''}
                  onChange={(e) => handleHeroSlideChange(index, 'title', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor={`hero_subtitle_${index}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subtitle</label>
                <textarea
                  id={`hero_subtitle_${index}`}
                  rows="3"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  value={slide.subtitle || ''}
                  onChange={(e) => handleHeroSlideChange(index, 'subtitle', e.target.value)}
                ></textarea>
              </div>
            </div>
          </div>
        ))}

        <div className="mt-6 flex space-x-3 justify-end">
          <button
            onClick={handleAddHeroSlide}
            className="flex items-center md:px-5 md:py-2.5 border border-accent dark:border-accent text-accent dark:text-accent rounded-lg hover:bg-primary-700dark:hover:bg-primary-700 transition duration-200 font-semibold text-sm"
          >
            <PlusCircle className="w-8 h-8 md:w-4 md:h-4 mr-2" /> Add New Slide
          </button>
          <button
            onClick={handleSaveHeroContent}
            className="flex items-center px-2 md:px-6 md:py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-primary-700 dark:hover:bg-primary-700 transition duration-200 font-semibold"
          >
            <Save className="w-4 h-4 mr-2" /> Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroContentManager;